import React, { useEffect, useRef, useState } from "react";
import { Modal, message, Input, Form, Button, Row, Col, Space } from "antd";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat, toLonLat } from "ol/proj";
import { Icon, Style } from "ol/style";
import { Translate } from "ol/interaction";
import Overlay from "ol/Overlay";
import { unByKey } from "ol/Observable";
import http from "@/utils/http";
import api from "@/utils/api";
import ipIcon from "@/assets/images/ipicon_3.png";
import ipIcon_1 from "@/assets/images/ipicon_1.png";
import { apply } from "ol-mapbox-style";
const LosAngelesMap = () => {
  const mapRef = useRef(null);
  const olMapRef = useRef(null);
  const vectorSourceRef = useRef(null);
  const vectorLayerRef = useRef(null);
  const translateInteractionRef = useRef(null);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);
  const clickKeyRef = useRef(null);
  const defaultStyleRef = useRef(null);
  const activeStyleRef = useRef(null);
  const activeFeatureRef = useRef(null);
  const [map, setMap] = useState(null);
  const [form] = Form.useForm();
  const [pagination] = useState({
    current: 1,
    pageSize: 10000,
    total: 0, 
  });


  const [isInputBoxVisible, setIsInputBoxVisible] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);

 useEffect(() => {
    let destroyed = false;
    const newMap = new Map({
      target: mapRef.current,
      view: new View({
        center: [-13150000, 4035000],
        zoom: 12,
        minZoom: 0,
        maxZoom: 20,
        projection: "EPSG:3857",
      }),
    });
    olMapRef.current = newMap;

    const initMap = async () => {
      try {
        await apply(
          newMap,
          "https://api.maptiler.com/maps/streets-v2/style.json?key=AQEg1jFXxCHAAxjqR7eI"
        );
      } catch (error) {
        console.error("Map style load failed:", error);
      }

      if (!destroyed) {
        initMapResources(newMap);
        setMap(newMap);
      }
    };

    initMap();

    return () => {
      destroyed = true;
      if (clickKeyRef.current) {
        unByKey(clickKeyRef.current);
        clickKeyRef.current = null;
      }
      if (translateInteractionRef.current) {
        newMap.removeInteraction(translateInteractionRef.current);
        translateInteractionRef.current = null;
      }
      if (overlayRef.current) {
        newMap.removeOverlay(overlayRef.current);
        overlayRef.current = null;
      }
      tooltipRef.current?.remove();
      tooltipRef.current = null;
      newMap.setTarget(null);
      olMapRef.current = null;
    };
  }, []);
  useEffect(() => {
    if (map) {
      fetchData();
    }
 
  }, [map]);
  useEffect(() => {
    if (selectedFeature) {
      const coordinates = selectedFeature.getGeometry().getCoordinates();
      const [longitude, latitude] = toLonLat(coordinates);
      form.setFieldsValue({
        longitude: longitude.toFixed(6),
        latitude: latitude.toFixed(6),
      });
    }
  }, [selectedFeature, form]);

  const getMarkerStyle = (active = false) => {
    const styleRef = active ? activeStyleRef : defaultStyleRef;
    const iconSrc = active ? ipIcon_1 : ipIcon;

    if (!styleRef.current) {
      styleRef.current = new Style({
        image: new Icon({
          anchor: [0.5, 1],
          src: iconSrc,
          scale: 0.8,
        }),
      });
    }

    return styleRef.current;
  };

  const resetActiveFeature = () => {
    if (activeFeatureRef.current?.setStyle) {
      activeFeatureRef.current.setStyle(getMarkerStyle(false));
    }
    activeFeatureRef.current = null;
  };

  const showTooltip = (feature, coordinate) => {
    const tooltip = tooltipRef.current;
    const overlay = overlayRef.current;
    if (!tooltip || !overlay) return;

    tooltip.innerHTML = `
      Company Name: ${feature.get("companyName") || ""}<br>
      Prop: ${feature.get("prop") || ""}<br>
      Group: ${feature.get("groupCode") || ""}<br>
      Property Name: ${feature.get("propName") || ""}<br>
      Street: ${feature.get("street") || ""}<br>
      City: ${feature.get("city") || ""}<br>
      State: ${feature.get("state") || ""}<br>
      Zip: ${feature.get("zip") || ""}<br>
      Prop Type: ${feature.get("propType") || ""}<br>
    `;
    tooltip.style.display = "block";
    overlay.setPosition(coordinate);
  };

  const hideTooltip = () => {
    if (!tooltipRef.current) return;
    tooltipRef.current.style.display = "none";
    tooltipRef.current.innerHTML = "";
  };

  function initMapResources(targetMap) {
    if (!vectorSourceRef.current) {
      vectorSourceRef.current = new VectorSource();
    }

    if (!vectorLayerRef.current) {
      vectorLayerRef.current = new VectorLayer({
        source: vectorSourceRef.current,
      });
      targetMap.addLayer(vectorLayerRef.current);
    }

    if (!tooltipRef.current) {
      const tooltip = document.createElement("div");
      tooltip.style.position = "absolute";
      tooltip.style.width = "300px";
      tooltip.style.height = "235px";
      tooltip.style.background = "white";
      tooltip.style.border = "1px solid #ccc";
      tooltip.style.fontSize = "16px";
      tooltip.style.display = "none";
      tooltip.style.padding = "20px";
      tooltip.style.borderRadius = "20px";
      document.body.appendChild(tooltip);
      tooltipRef.current = tooltip;
    }

    if (!overlayRef.current) {
      overlayRef.current = new Overlay({
        element: tooltipRef.current,
        offset: [10, 0],
        positioning: "bottom-left",
      });
      targetMap.addOverlay(overlayRef.current);
    }

    if (!translateInteractionRef.current) {
      const translateInteraction = new Translate({
        layers: [vectorLayerRef.current],
      });

      translateInteraction.setActive(false);
      translateInteraction.on("translateend", (event) => {
        translateInteraction.setActive(false);
        const feature = event.features.getArray()[0];
        const newCoords = feature.getGeometry().getCoordinates();
        const newLonLat = toLonLat(newCoords);

        Modal.confirm({
          title: "Confirm update location",
          content: "Do you want to update the location of this point?",
          onOk: () => {
            updateLocation(feature.get("propId"), newLonLat[0], newLonLat[1], feature);
          },
          onCancel: () => {
            feature.getGeometry().setCoordinates(feature.get("originalCoords"));
          },
        });
      });

      targetMap.addInteraction(translateInteraction);
      translateInteractionRef.current = translateInteraction;
    }

    if (!clickKeyRef.current) {
      clickKeyRef.current = targetMap.on("click", (event) => {
        if (event.originalEvent.shiftKey) {
          translateInteractionRef.current?.setActive(true);
          return;
        }

        const feature = targetMap.forEachFeatureAtPixel(
          event.pixel,
          (feature, layer) => {
            if (layer !== vectorLayerRef.current || !feature?.setStyle) {
              return null;
            }
            return feature;
          }
        );
        const viewport = targetMap.getViewport();

        if (feature) {
          viewport.classList.add("clickable");
        } else {
          viewport.classList.remove("clickable");
        }

        if (!feature) {
          resetActiveFeature();
          setSelectedFeature(null);
          setIsInputBoxVisible(false);
          hideTooltip();
          return;
        }

        if (
          activeFeatureRef.current?.setStyle &&
          activeFeatureRef.current !== feature
        ) {
          activeFeatureRef.current.setStyle(getMarkerStyle(false));
        }

        feature.setStyle(getMarkerStyle(true));
        activeFeatureRef.current = feature;
        setIsInputBoxVisible(true);
        setSelectedFeature(feature);

        const coordinates = feature.getGeometry().getCoordinates();
        const [longitude, latitude] = toLonLat(coordinates);
        form.setFieldsValue({
          longitude: longitude.toFixed(6),
          latitude: latitude.toFixed(6),
        });

        showTooltip(feature, event.coordinate);
      });
    }
  }

  const fetchData = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: pagination.current || 1,
        count: pagination.pageSize || 10000,
        status: "P",
      });
      console.log(queryParams);
      const response = await http.get(
        `${api.showProperty}?${queryParams.toString()}`
      );

      const validProperties = response.data.list
        .filter((item) => item.longitude && item.latitude) // 只保留有经纬度的数据
        .map((item) => ({
          longitude: parseFloat(item.longitude), // 确保是数值
          latitude: parseFloat(item.latitude),
          propName: item.propName,
          propId: item.propId,
          prop: item.prop,
          groupCode: item.groupCode,
          street: item.street,
          city: item.city,
          state: item.state,
          zip: item.zip,
          propType: item.propType,
          companyName: item.companyName,
        }));

      addMarkers(validProperties);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  const addMarkers = (properties) => {
    if (!vectorSourceRef.current) return;

    resetActiveFeature();
    setSelectedFeature(null);
    setIsInputBoxVisible(false);
    hideTooltip();
    vectorSourceRef.current.clear();

    properties.forEach((property) => {
      const longitude = parseFloat(property.longitude);
      const latitude = parseFloat(property.latitude);
      const coords = fromLonLat([longitude, latitude]);
      const marker = new Feature({
        geometry: new Point(coords),
        propName: property.propName,
        propId: property.propId,
        prop: property.prop,
        groupCode: property.groupCode,
        street: property.street,
        city: property.city,
        state: property.state,
        zip: property.zip,
        propType: property.propType,
        companyName: property.companyName,
        originalCoords: coords,
      });

      marker.setStyle(getMarkerStyle(false));
      vectorSourceRef.current.addFeature(marker);
    });
  };

  
  const updateLocation = async (propId, longitude, latitude, feature) => {
    try {
      console.log("点位信息", propId, longitude, latitude);
      const data = {
        propId: propId,
        longitude: longitude,
        latitude: latitude,
      };
      const response = await http.post(`${api.getPropertyUpdate}`, data);
      if (response.code === "1") {
        message.success("Location updated successfully!");
        const nextLongitude = response.data?.longitude ?? longitude;
        const nextLatitude = response.data?.latitude ?? latitude;
        const nextCoords = fromLonLat([nextLongitude, nextLatitude]);

        feature?.getGeometry()?.setCoordinates(nextCoords);
        feature?.set("originalCoords", nextCoords);

        if (feature && selectedFeature === feature) {
          form.setFieldsValue({
            longitude: Number(nextLongitude).toFixed(6),
            latitude: Number(nextLatitude).toFixed(6),
          });
        }
      }
    } catch (error) {
      console.error("Error updating location:", error);
      message.error("Failed to update the location.");
    }
  };
  // 表单提交
  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      const { longitude, latitude } = values;

      if (!selectedFeature) {
        message.error("No feature selected!");
        return;
      }

      const data = {
        propId: selectedFeature.get("propId"),
        longitude: parseFloat(longitude),
        latitude: parseFloat(latitude),
      };

      console.log("提交数据:", data);

      let response;
      try {
        response = await http.post(`${api.getPropertyUpdate}`, data);
        console.log("API 响应:", response);
      } catch (apiError) {
        console.error("API 请求失败:", apiError);
        message.error("Failed to update location.");
        return;
      }

      if (response?.code === "1") {
        message.success("Location updated successfully!");

        const updatedLongitude = response.data?.longitude ?? data.longitude;
        const updatedLatitude = response.data?.latitude ?? data.latitude;
        const newCoordinates = fromLonLat([updatedLongitude, updatedLatitude]);

        if (selectedFeature) {
          selectedFeature.getGeometry().setCoordinates(newCoordinates);
          selectedFeature.set("originalCoords", newCoordinates);
          selectedFeature.setStyle(getMarkerStyle(true));
        }
        hideTooltip();

        map.getView().setCenter(newCoordinates);
      } else {
        message.error(
          "Failed to update location. Server response was invalid."
        );
      }
    } catch (error) {
      console.error("未知错误:", error);
      message.error("Failed to update location.");
    }
  };

  const getGoogleMap = async () => {
    const street = selectedFeature?.get("street");
    const city = selectedFeature?.get("city");
    const state = selectedFeature?.get("state");
    const zip = selectedFeature?.get("zip");

    const fullAddress = `${street}, ${city}, ${state}, ${zip}`;

    const encodedAddress = encodeURIComponent(fullAddress);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, "_blank");
  };

  return (
    <div>
      <div ref={mapRef} style={{ width: "100%", height: "100vh" }}></div>

      {isInputBoxVisible && (
        <div className="inputBox">
          <Form form={form} name="dynamic_rule">
            <Row gutter={[12, 8]} align="start">
              <Col flex="1 1 180px">
                <Form.Item name="longitude" label="Longitude">
                  <Input placeholder="Please input longitude" />
                </Form.Item>
              </Col>
              <Col flex="1 1 180px">
                <Form.Item name="latitude" label="Latitude">
                  <Input placeholder="Please input latitude" />
                </Form.Item>
              </Col>
              <Col flex="0 0 auto">
                <Space>
                  <Button type="primary" onClick={handleUpdate}>
                    Update
                  </Button>
                  <Button type="primary" onClick={getGoogleMap}>
                    Google Map
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </div>
  );
};

export default LosAngelesMap;
